import { Box, CreditCardTransactionResponse } from 'vopay';
import { FunctionComponent } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TransactionCard from '@/components/Entities/UI/TransactionCard/TransactionCard';
import TransactionDetailsCard from '@/components/Entities/UI/TransactionDetailsCard/TransactionDetailsCard';
import ErrorInfoCard from '@/components/Entities/UI/ErrorInfoCard/ErrorInfoCard';

type CreditCardTransactionInfoProps = {
  transaction: CreditCardTransactionResponse;
};

type FC = FunctionComponent<CreditCardTransactionInfoProps>;

const CreditCardTransactionInfo: FC = ({ transaction }) => {
  const { accountId } = useParams<{ accountId: string }>();
  const accountName = new URLSearchParams(useLocation().search).get('accountName') || '';

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.Details.TransactionDetails',
  });

  const transactionDetails = [
    {
      label: t('Label.transactionType', { defaultValue: 'Transaction Type' }),
      value: t('TransactionType.creditCard', { defaultValue: 'Credit Card' }),
    },
    {
      label: t('Label.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('Label.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime || '-',
    },
    {
      label: t('Label.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified || '-',
    },
    {
      label: t('Label.amount', { defaultValue: 'Amount' }),
      value: transaction.Amount || '-',
    },
    {
      label: t('Label.currency', { defaultValue: 'Currency' }),
      value: transaction.Currency || '-',
    },
    {
      label: t('Label.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount || '-',
    },
    {
      label: t('Label.country', { defaultValue: 'Country' }),
      value: transaction.Country || '-',
    },
    {
      label: t('Label.clientAccountId', { defaultValue: 'Client Account ID' }),
      value: transaction.ClientAccountID || '-',
    },
    {
      label: t('Label.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('Label.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('Label.notes', { defaultValue: 'Notes' }),
      value: transaction.Notes || '-',
    },
  ];

  const creditCardDetails = [
    {
      label: t('Label.creditCardToken', { defaultValue: 'Credit Card Token' }),
      value: transaction.CreditCardToken || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={accountName || '-'}
        amount={transaction.Amount}
        currency={transaction.Currency}
        transactionId={transaction.TransactionID}
        type="Credit Card"
        accountId={accountId}
        failureReason={transaction.FailureReason}
      />

      <TransactionDetailsCard
        title={t('Title.transactionDetails', { defaultValue: 'Transaction Details' })}
        details={transactionDetails}
      />

      <TransactionDetailsCard
        title={t('Title.creditCardDetails', { defaultValue: 'Credit Card Details' })}
        details={creditCardDetails}
      />

      <ErrorInfoCard
        currency={transaction.Currency}
        amount={transaction.Amount}
        lastModified={transaction.LastModified}
        failureReason={transaction.FailureReason}
      />
    </Box>
  );
};

export default CreditCardTransactionInfo;
